import { ArrowForwardIos, FiberManualRecord } from '@mui/icons-material';
import { IconButton, Skeleton, Typography, useTheme } from '@mui/material';
import { ScreenType } from '@tmdb-graphql-api/schema/types';
import { useMemo, useState } from 'react';
import { Link } from '#components/Link/index.tsx';
import { PlayerSelectorTabs } from '#components/PlayerSelectorTabs/index.tsx';
import { Section, SectionColor } from '#components/Section/index.tsx';
import { useIsAncestor } from '#contexts/Ancestors/index.ts';
import { Ancestor } from '#enums/ancestors.ts';
import { ImageSize, StillSize } from '#enums/imageSizes.ts';
import { ScreenFormatType, SectionType } from '#enums/mediaTypes.ts';
import { buildImagePath } from '#helpers/buildImagePath.ts';
import { formatAirDate } from '#helpers/formatAirDate.ts';
import { formatGenres } from '#helpers/formatGenres.ts';
import { formatRuntime } from '#helpers/formatRuntime.ts';
import { disable as disableFullscreenLock } from '#helpers/fullscreenLock.ts';
import { getTvCertFromContentRatings } from '#helpers/getTvCertFromContentRatings.ts';
import { get } from '#helpers/otherHelpers.ts';
import { useToolbelt } from '#hooks/useToolbelt.ts';
import { SectionHeading } from '#styled.ts';
import { type ActiveVideo } from '#types.ts';
import { BackdropImage } from '../../BackdropImage/index.tsx';
import { CastAndCrewCarousel } from '../../CastAndCrew/Carousel/index.tsx';
import { Query as CastAndCrewHighlights } from '../../CastAndCrew/Highlights/index.ts';
import { Component as CertificationIcon } from '../../CertificationIcon/index.ts';
import { ConditionalTextLink } from '../../ConditionalTextLink/index.tsx';
import { EpisodesCarousel } from '../../EpisodesCarousel/index.tsx';
import { PlayerSelectorModal } from '../../Modal/PlayerSelectorModal.tsx';
import { Overview } from '../../Overview/index.tsx';
import { RecommendationsCarousel } from '../../RecommendationsCarousel/index.tsx';
import { SeasonsCarousel } from '../../SeasonsCarousel/index.tsx';
import { TextLink } from '../../TextLink/index.tsx';
import { Large as VoteAverageBadge } from '../../VoteAverageBadge/index.tsx';
import { JustWatchLogo } from '../../WatchProviders/JustWatchLogo.tsx';
import { WatchProvidersQuery } from '../../WatchProviders/index.ts';
import {
  CastAndCrewHighlightsContainer,
  CertificationIconContainer,
  DetailsBody,
  PoweredBy,
  RatingsContainer,
  SectionA,
  StillImageContainer,
  StillImageOverviewContainer,
  WatchProvidersHeadingContainer,
} from '../styled.ts';
import { type Props } from './types.ts';

export const EpisodeDetails = ({ data, setActivePreview }: Props) => {
  const { airDate, episodeNumber, id, name, overview, runtime, stillPath, voteAverage, voteCount } = data.episode;
  const { id: seasonId, seasonNumber } = data.season;
  const { contentRatings, genres, id: tvId, name: tvName } = data.tv;
  const theme = useTheme();
  const { config, uaParser } = useToolbelt();
  const [activeVideo, setActiveVideo] = useState<ActiveVideo | undefined>();
  const isAncestorSlideoutDrawer = useIsAncestor(Ancestor.ListingPreview);
  const certification = getTvCertFromContentRatings(contentRatings ?? [], config.country);
  const formattedGenres = formatGenres(genres);
  const formattedRuntime = formatRuntime(runtime);
  const episodePrefix = `Episode ${String(episodeNumber)}`;

  const memoTvDetails = useMemo(
    () => (
      <>
        <Section threeQuarterWidthOnDesktop={!isAncestorSlideoutDrawer}>
          <Typography sx={{ paddingRight: '52px', position: 'relative' }} variant="h4">
            <ConditionalTextLink link={isAncestorSlideoutDrawer} to={`/details/episode/${String(id)}`}>
              <strong>{name === episodePrefix ? name : `${episodePrefix}: ${name}`}</strong>
            </ConditionalTextLink>
            {isAncestorSlideoutDrawer ? (
              <IconButton
                aria-label="Open preview in full"
                component={Link}
                sx={{
                  color: theme.palette.grey[100],
                  position: 'absolute',
                  right: 0,
                  top: 0,
                }}
                to={`/details/episode/${String(id)}`}
              >
                <ArrowForwardIos />
              </IconButton>
            ) : undefined}
          </Typography>
          <Typography variant="h6">
            <TextLink
              to={`/details/season/${String(seasonId)}`}
            >{`${tvName}, Season ${String(seasonNumber)}`}</TextLink>
          </Typography>
          <div>
            <Typography sx={{ color: theme.supportingTextPrimary }} variant="body2">
              {airDate ? <span>{formatAirDate(airDate, 'MMM d, yyyy')}</span> : undefined}
              {formattedGenres ? (
                <span>
                  <FiberManualRecord
                    sx={{ height: '10px', marginLeft: theme.spacing(1), marginRight: theme.spacing(1), width: '10px' }}
                  />
                  <span>{formattedGenres}</span>
                </span>
              ) : undefined}
              {formattedRuntime ? (
                <>
                  <FiberManualRecord
                    sx={{ height: '10px', marginLeft: theme.spacing(1), marginRight: theme.spacing(1), width: '10px' }}
                  />
                  <span>{formattedRuntime}</span>
                </>
              ) : undefined}
            </Typography>
          </div>
          <SectionA sx={{ display: 'flex', marginBottom: theme.spacing(1.5), marginTop: theme.spacing(1.5) }}>
            {certification ? (
              <CertificationIconContainer>
                <CertificationIcon
                  certification={certification}
                  height={50}
                  id={tvId}
                  type={ScreenType.Tv}
                  width={50}
                />
              </CertificationIconContainer>
            ) : undefined}
            <RatingsContainer>
              <VoteAverageBadge
                sx={{
                  [theme.breakpoints.up('md')]: {
                    marginLeft: theme.spacing(2),
                  },
                  marginLeft: theme.spacing(1),
                }}
                voteAverage={voteAverage}
                voteCount={voteCount}
              />
            </RatingsContainer>
          </SectionA>
        </Section>
        <Section color={SectionColor.Black}>
          <PlayerSelectorTabs
            episodeNumber={episodeNumber}
            id={tvId}
            seasonNumber={seasonNumber}
            title={tvName}
            type={ScreenFormatType.Episode}
          />
        </Section>
        <Section threeQuarterWidthOnDesktop={!isAncestorSlideoutDrawer}>
          <DetailsBody>
            <StillImageContainer>
              <BackdropImage
                src={buildImagePath(stillPath, ImageSize.Still, StillSize.XL, config.images)}
                title={name}
                type={SectionType.Episode}
              />
            </StillImageContainer>
            <StillImageOverviewContainer>
              {overview ? <Overview content={overview} id={id} name={name} /> : undefined}
            </StillImageOverviewContainer>
            <CastAndCrewHighlightsContainer
              sx={{
                [theme.breakpoints.down('sm')]: {
                  gridColumnStart: 1,
                  gridRowStart: 'auto',
                },
                ...(overview
                  ? {
                      gridColumnEnd: 5,
                      gridColumnStart: 1,
                      gridRowStart: 3,
                    }
                  : {
                      gridColumnStart: 3,
                    }),
                marginTop: overview ? theme.spacing(2) : undefined,
              }}
            >
              <CastAndCrewHighlights id={id} type={ScreenFormatType.Episode} />
            </CastAndCrewHighlightsContainer>
          </DetailsBody>
        </Section>
        {!isAncestorSlideoutDrawer && (
          <>
            <WatchProvidersQuery id={tvId} type={ScreenType.Tv}>
              {watchProviders =>
                watchProviders ? (
                  <Section
                    color={SectionColor.Grey}
                    threeQuarterWidthOnDesktop={!isAncestorSlideoutDrawer}
                    title={
                      <WatchProvidersHeadingContainer>
                        {get<number>(watchProviders, 'props.providers.length') > 0 ? (
                          <>
                            <SectionHeading variant="h6">Where to watch</SectionHeading>
                            <div>
                              <PoweredBy>Powered by</PoweredBy>
                              <JustWatchLogo />
                            </div>
                          </>
                        ) : (
                          <Skeleton sx={{ fontSize: '1.25rem' }} variant="text" width={120} />
                        )}
                      </WatchProvidersHeadingContainer>
                    }
                  >
                    {watchProviders}
                  </Section>
                ) : undefined
              }
            </WatchProvidersQuery>
            <EpisodesCarousel id={seasonId} seasonNumber={seasonNumber} setActivePreview={setActivePreview} tvId={tvId}>
              {episodesCarousel =>
                episodesCarousel ? <Section title="Episodes">{episodesCarousel}</Section> : undefined
              }
            </EpisodesCarousel>
            <CastAndCrewCarousel
              id={id}
              setActivePreview={setActivePreview}
              subType="cast"
              type={ScreenFormatType.Episode}
            >
              {castCarousel => (castCarousel ? <Section title="Cast">{castCarousel}</Section> : undefined)}
            </CastAndCrewCarousel>
            <CastAndCrewCarousel
              id={id}
              setActivePreview={setActivePreview}
              subType="guestStars"
              type={ScreenFormatType.Episode}
            >
              {guestStarsCarousel =>
                guestStarsCarousel ? <Section title="Guest stars">{guestStarsCarousel}</Section> : undefined
              }
            </CastAndCrewCarousel>
            <CastAndCrewCarousel
              id={id}
              setActivePreview={setActivePreview}
              subType="crew"
              type={ScreenFormatType.Episode}
            >
              {castCarousel => (castCarousel ? <Section title="Crew">{castCarousel}</Section> : undefined)}
            </CastAndCrewCarousel>
            <SeasonsCarousel id={tvId} setActivePreview={setActivePreview}>
              {seasonsCarousel => (seasonsCarousel ? <Section title="Seasons">{seasonsCarousel}</Section> : undefined)}
            </SeasonsCarousel>
            <RecommendationsCarousel id={tvId} setActivePreview={setActivePreview} type={ScreenType.Tv}>
              {recommendationsCarousel =>
                recommendationsCarousel ? <Section title="Recommended">{recommendationsCarousel}</Section> : undefined
              }
            </RecommendationsCarousel>
          </>
        )}
      </>
    ),
    [id], // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <div>
      {activeVideo?.src && activeVideo.src.length > 0 ? (
        <PlayerSelectorModal
          activeVideo={activeVideo}
          onClose={() => {
            setActiveVideo(undefined);
            void disableFullscreenLock(uaParser);
          }}
          type={ScreenType.Tv}
        />
      ) : undefined}
      {memoTvDetails}
    </div>
  );
};
